import { useEffect, useMemo, useState } from 'react';
import { ApiRequest } from '@/utils/axios';
import { inventoryStore } from '@/stores/inventory.store';
import { modalStore } from '@/stores/modal.store';
import { notify } from '@/utils/notify';
import SuccessIcon from '@/components/Notifications/SuccessIcon';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import CoinflipInventoryPicker from '@/components/Modals/Inventory/CoinflipInventoryPicker';
import CoinflipJoinFooter from '@/components/Modals/Inventory/CoinflipJoinFooter';

interface Props {
    minbet: number;
}

const JackpotDeposit = ({ minbet }: Props) => {
    const {
        inventoryItems,
        selectedItemIds,
        maxSelectedItems,
        setSelectedById,
        clearInventory,
        loadSiteInventoryDeposit,
    } = inventoryStore((state) => state);
    const { setTitle, setComponent, close, toggleModal } = modalStore((state) => state);

    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            await loadSiteInventoryDeposit();
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load().then();
        return () => clearInventory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pickerItems = useMemo(
        () =>
            inventoryItems.map((it) => ({
                id: it.id,
                name: it.item.name,
                priceUsd: it.item.priceUsd,
                colorHex: it.item.colorHex,
                icon: it.item.icon,
            })),
        [inventoryItems]
    );

    const inventoryTotal = pickerItems.reduce((s, i) => s + i.priceUsd, 0);
    const selected = pickerItems.filter((i) => selectedItemIds.has(i.id));
    const selectedAmount = selected.reduce((s, i) => s + i.priceUsd, 0);
    const canBet = selectedAmount >= minbet;

    async function jackpotClosed(): Promise<boolean> {
        const {
            data: { response },
        } = await ApiRequest.get('/jackpot');
        return response.state.finishedAt != null;
    }

    async function bet() {
        if (!canBet) {
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title={'Error'} text={`Min bet is $${minbet}`} />
            );
            return;
        }
        setTitle('Loading...');
        setComponent('Loader');

        if (await jackpotClosed()) {
            close();
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title={'Error'} text={`Jackpot is in finish state, try later`} />
            );
            return;
        }

        const inventoryIds = Array.from(selectedItemIds);
        const {
            data: { success },
        } = await ApiRequest.post('/jackpot/join/inventory', { inventoryIds });

        if (success) {
            toggleModal(false);
            notify(
                'success',
                <SuccessIcon />,
                <NotifyWrapper title={'Success'} text={'You successfully joined jackpot!'} />
            );
        }
    }

    return (
        <CoinflipInventoryPicker
            items={pickerItems}
            selectedItemIds={selectedItemIds}
            setSelectedById={setSelectedById}
            maxItems={maxSelectedItems}
            loading={loading}
            onRefresh={() => load()}
            footer={
                <CoinflipJoinFooter
                    inventoryTotal={inventoryTotal}
                    selectedCount={selected.length}
                    selectedAmount={selectedAmount}
                    onAction={bet}
                    actionLabel="MAKE DEPOSIT"
                    actionDisabled={!canBet}
                />
            }
        />
    );
};

export default JackpotDeposit;
